import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { greeting, socialMediaLinks, projects } from "../portfolio";
import {
  FiSearch,
  FiTerminal,
  FiFileText,
  FiSun,
  FiMoon,
  FiCopy,
  FiExternalLink,
  FiArrowRight,
  FiCornerDownLeft,
  FiCheck,
} from "react-icons/fi";
import { playClick, playHover, playKeyClick } from "../utils/soundEngine";

export default function CommandPalette({
  isOpen,
  setIsOpen,
  toggleCliMode,
  toggleTheme,
  darkMode,
}) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();

  // Global Keyboard Listener for ⌘K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsOpen((prev) => {
          if (!prev) playClick();
          return !prev;
        });
      } else if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setIsOpen]);

  // Instant focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      const timer = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const copyEmail = useCallback(() => {
    navigator.clipboard.writeText(socialMediaLinks.gmail);
    setCopied(true);
    playClick();
    setTimeout(() => {
      setCopied(false);
      setIsOpen(false);
    }, 900);
  }, [setIsOpen]);

  const scrollTo = useCallback(
    (id) => {
      setIsOpen(false);
      playClick();
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
      }
    },
    [setIsOpen]
  );

  // Define commands catalog
  const commands = [
    {
      category: "Quick Actions",
      id: "action-cli",
      title: "Switch to Terminal Mode",
      subtitle: "Open interactive CLI terminal portfolio",
      icon: FiTerminal,
      action: () => {
        setIsOpen(false);
        playClick();
        toggleCliMode();
      },
    },
    {
      category: "Quick Actions",
      id: "action-theme",
      title: `Switch to ${darkMode ? "Light" : "Dark"} Mode`,
      subtitle: "Toggle visual color theme",
      icon: darkMode ? FiSun : FiMoon,
      action: () => {
        setIsOpen(false);
        playClick();
        toggleTheme();
      },
    },
    {
      category: "Quick Actions",
      id: "action-resume",
      title: "Open PDF Resume",
      subtitle: "View full software engineering resume",
      icon: FiFileText,
      action: () => {
        setIsOpen(false);
        playClick();
        window.open(greeting.resumeLink, "_blank");
      },
    },
    {
      category: "Quick Actions",
      id: "action-copy-email",
      title: copied ? "Email Copied!" : "Copy Email Address",
      subtitle: socialMediaLinks.gmail,
      icon: copied ? FiCheck : FiCopy,
      action: copyEmail,
    },
    // Navigation jumps
    {
      category: "Navigation",
      id: "nav-skills",
      title: "Jump to Skills & Capabilities",
      subtitle: "Core architecture, tech stack & AI workflows",
      icon: FiArrowRight,
      action: () => scrollTo("skills"),
    },
    {
      category: "Navigation",
      id: "nav-experience",
      title: "Jump to Experience & Career",
      subtitle: "Cohesity, Veritas & production milestones",
      icon: FiArrowRight,
      action: () => scrollTo("experience"),
    },
    {
      category: "Navigation",
      id: "nav-projects",
      title: "Jump to Featured Projects",
      subtitle: "Neon Overdrive, Aero Nova, CAD & Pokédex OS",
      icon: FiArrowRight,
      action: () => scrollTo("projects"),
    },
    {
      category: "Navigation",
      id: "nav-blogs",
      title: "Jump to Technical Writing",
      subtitle: "DEV.to published articles & guides",
      icon: FiArrowRight,
      action: () => scrollTo("blogs"),
    },
    {
      category: "Navigation",
      id: "nav-awards",
      title: "Jump to Awards & Publications",
      subtitle: "Scholarships, algorithm contests & research papers",
      icon: FiArrowRight,
      action: () => scrollTo("awards"),
    },
    {
      category: "Navigation",
      id: "nav-contact",
      title: "Jump to Contact",
      subtitle: "Get in touch for engineering roles",
      icon: FiArrowRight,
      action: () => scrollTo("contact"),
    },
    // Direct Project Jumps
    ...projects.data.map((p) => ({
      category: "Projects",
      id: `proj-${p.name}`,
      title: p.name,
      subtitle: p.description.slice(0, 70) + "...",
      icon: FiExternalLink,
      action: () => {
        setIsOpen(false);
        playClick();
        if (p.url) {
          window.open(p.url, "_blank");
        } else {
          scrollTo("projects");
        }
      },
    })),
  ];

  // Filter commands by search query
  const filtered = commands.filter(
    (c) =>
      c.title.toLowerCase().includes(query.toLowerCase()) ||
      c.subtitle.toLowerCase().includes(query.toLowerCase()) ||
      c.category.toLowerCase().includes(query.toLowerCase())
  );

  // Keyboard navigation within list
  const handleInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(filtered.length, 1));
      playHover();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(
        (prev) => (prev - 1 + filtered.length) % Math.max(filtered.length, 1)
      );
      playHover();
    } else if (e.key === "Enter" && filtered[selectedIndex]) {
      e.preventDefault();
      filtered[selectedIndex].action();
    } else if (
      e.key !== "Escape" &&
      e.key !== "Meta" &&
      e.key !== "Control" &&
      e.key !== "Alt" &&
      e.key !== "Shift"
    ) {
      playKeyClick();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-20 sm:pt-28 px-4">
          {/* Backdrop blur overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: shouldReduceMotion ? 0.01 : 0.12 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-md"
          />

          {/* Palette Modal Box */}
          <motion.div
            initial={
              shouldReduceMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.98, y: -4 }
            }
            animate={
              shouldReduceMotion
                ? { opacity: 1 }
                : { opacity: 1, scale: 1, y: 0 }
            }
            exit={
              shouldReduceMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.98, y: -4 }
            }
            transition={{
              duration: shouldReduceMotion ? 0.01 : 0.11,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="relative w-full max-w-xl rounded-2xl border border-surface/[0.12] bg-bg-raised/95 backdrop-blur-2xl shadow-[0_24px_64px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col z-10"
          >
            {/* Search Input Bar */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-surface/[0.08]">
              <FiSearch className="text-fg-faint text-lg shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                onKeyDown={handleInputKeyDown}
                placeholder="Type a command or search sections..."
                className="w-full bg-transparent text-sm text-fg placeholder:text-fg-faint focus:outline-none font-sans"
              />
              <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 rounded bg-surface/[0.06] border border-surface/[0.08] text-[10px] font-mono text-fg-faint">
                ESC
              </kbd>
            </div>

            {/* Results List */}
            <div
              ref={listRef}
              className="max-h-[360px] overflow-y-auto p-2 space-y-1"
            >
              {filtered.length === 0 ? (
                <div className="py-10 text-center text-xs font-mono text-fg-faint">
                  No matching commands found.
                </div>
              ) : (
                filtered.map((item, idx) => {
                  const Icon = item.icon;
                  const isSelected = idx === selectedIndex;

                  return (
                    <button
                      key={item.id}
                      onClick={item.action}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={`w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl text-left transition-all duration-150 ${
                        isSelected
                          ? "bg-surface/[0.08] text-fg"
                          : "text-fg-muted hover:bg-surface/[0.04]"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                            isSelected
                              ? "bg-fg text-bg"
                              : "bg-surface/[0.05] text-fg-faint"
                          }`}
                        >
                          <Icon size={14} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-fg truncate">
                            {item.title}
                          </div>
                          <div className="text-[11px] font-mono text-fg-faint truncate">
                            {item.subtitle}
                          </div>
                        </div>
                      </div>

                      {isSelected && (
                        <div className="flex items-center gap-1 text-[10.5px] font-mono text-fg-faint shrink-0">
                          <span>Select</span>
                          <FiCornerDownLeft size={11} />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer Bar */}
            <div className="px-4 py-2.5 bg-surface/[0.02] border-t border-surface/[0.06] flex items-center justify-between text-[10.5px] font-mono text-fg-faint">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1 rounded bg-surface/[0.06] border border-surface/[0.08]">
                    ↑
                  </kbd>
                  <kbd className="px-1 rounded bg-surface/[0.06] border border-surface/[0.08]">
                    ↓
                  </kbd>
                  <span>navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 rounded bg-surface/[0.06] border border-surface/[0.08]">
                    ↵
                  </kbd>
                  <span>execute</span>
                </span>
              </div>
              <span className="hidden sm:inline">Commands</span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
